// Web-Audio notification sounds for the live tournament tracker. No audio
// assets — every built-in "set" is synthesized from oscillator tones, so it
// works offline and adds zero bytes. A set can instead declare sample URLs
// (kind:'sample') for ripped audio dropped in later; playEvent handles both.
//
// EVENTS:
//   newTournament — the current tournament finished; a new join window opens.
//   matchUp       — a tracked unit's match just became the current bracket match.
//   win / loss    — a tracked unit's match resolved.
//   champ         — a tracked unit's team won the championship match.

// One shared AudioContext, created lazily on first play (browsers require a
// user gesture first; by the time anything fires here the user has interacted).
let audioCtx = null;
function ctx() {
  try {
    if (!audioCtx) {
      const C = window.AudioContext || window.webkitAudioContext;
      if (!C) return null;
      audioCtx = new C();
    }
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    return audioCtx;
  } catch { return null; }
}

// Note sequences per event — [freq Hz, start s, dur s] triples. Shared across
// the tone-based sets; each set re-timbres them via its wave + gain. The
// sequences are designed to be recognizable: rising = good, falling = bad,
// champ = a little da-da-da-DAA fanfare.
const SEQ = {
  newTournament: [[880, 0, 0.18], [1320, 0.13, 0.4]],                                  // the original two-tone ding
  matchUp:       [[659, 0, 0.1], [880, 0.09, 0.1], [1175, 0.18, 0.32]],                // quick rising "you're up"
  win:           [[523, 0, 0.12], [659, 0.1, 0.12], [784, 0.2, 0.12], [1046, 0.32, 0.4]], // ascending major arpeggio
  loss:          [[523, 0, 0.16], [415, 0.15, 0.16], [311, 0.32, 0.5]],                // descending, minor
  champ:         [[523, 0, 0.13], [523, 0.13, 0.13], [523, 0.26, 0.13], [659, 0.42, 0.16], [784, 0.62, 0.18], [1046, 0.82, 0.6]], // fanfare
};

// Built-in sets. `wave` = oscillator type, `gain` = peak amplitude. `label` for
// the picker. To add ripped FFT audio later: add a set with kind:'sample' and a
// urls:{ event: '/sounds/foo.ogg', … } map (ogg/mp3, small files).
export const SOUND_SETS = {
  chime: { label: 'Chime (soft)', wave: 'sine', gain: 0.16 },
  retro: { label: 'Retro (8-bit)', wave: 'square', gain: 0.08 },
  bell:  { label: 'Bell (bright)', wave: 'triangle', gain: 0.15 },
};
export const DEFAULT_SOUND_SET = 'chime';

// User volume multiplier: 1 = 100% (the tuned per-set default), 0 = muted, up to
// 2 = 200%. Clamped + coerced so a bad stored value can't blow out the eardrums or
// break the Web-Audio ramp. Pure — shared with userPrefs + tested.
export const clampVolume = (v) => { v = Number(v); return Number.isFinite(v) ? Math.max(0, Math.min(2, v)) : 1; };

function playTones(set, seq, volume) {
  const c = ctx();
  if (!c || !seq) return;
  const peak = set.gain * volume;
  if (peak <= 0.0001) return; // muted (also keeps the exponential ramp target positive)
  const t0 = c.currentTime;
  for (const [freq, start, dur] of seq) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.connect(g); g.connect(c.destination);
    o.type = set.wave;
    o.frequency.setValueAtTime(freq, t0 + start);
    g.gain.setValueAtTime(0.0001, t0 + start);
    g.gain.exponentialRampToValueAtTime(peak, t0 + start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
    o.start(t0 + start);
    o.stop(t0 + start + dur + 0.03);
    o.onended = () => { try { o.disconnect(); g.disconnect(); } catch { /* ignore */ } };
  }
}

function playSample(set, event, volume) {
  const url = set.urls && set.urls[event];
  if (!url) return;
  const vol = Math.max(0, Math.min(1, (set.volume ?? 0.6) * volume)); // HTMLAudio caps at 1.0
  if (vol <= 0) return;
  try { const a = new Audio(url); a.volume = vol; a.play().catch(() => {}); } catch { /* ignore */ }
}

// Play `event` from `setId` at `volume`× the set's tuned level (1 = 100%, 0–2).
// Silent no-op if the set/event is unknown or audio is unavailable. The CALLER
// gates on the user's per-event toggle — this always plays (so the settings "test"
// buttons can preview a disabled event).
export function playEvent(setId, event, volume = 1) {
  const set = SOUND_SETS[setId] || SOUND_SETS[DEFAULT_SOUND_SET];
  if (!set) return;
  const v = clampVolume(volume);
  if (set.kind === 'sample') playSample(set, event, v);
  else playTones(set, SEQ[event], v);
}

// Convenience: play `event` only if the user's prefs enable it, at the user's
// chosen volume (prefs.soundVolume, default 100%). Used by the poll-driven match
// notifications.
export function playEventIfEnabled(prefs, event) {
  if (prefs && prefs.soundEvents && prefs.soundEvents[event]) {
    playEvent(prefs.soundSet || DEFAULT_SOUND_SET, event, prefs.soundVolume);
  }
}

// The QF team pairs, in bracket-match order. winners[0..3] are these, [4]/[5]
// the semis, [6] the final, [7] the champ match (final winner vs. the standing
// champion team). Mirrors the bracket layout in Luxstadio.
const QF_PAIRS = [['red', 'blue'], ['green', 'yellow'], ['white', 'black'], ['purple', 'brown']];

// The two team colors contesting bracket match `i`, given the winners array.
// Returns [a, b] color names, or null if not yet determined. (Champ match: the
// final's winner vs. the persistent 'champion' team.)
export function matchTeams(i, winners) {
  const w = winners || [];
  if (i < 4) return QF_PAIRS[i];
  if (i === 4) return (w[0] && w[1]) ? [w[0], w[1]] : null;
  if (i === 5) return (w[2] && w[3]) ? [w[2], w[3]] : null;
  if (i === 6) return (w[4] && w[5]) ? [w[4], w[5]] : null;
  if (i === 7) return w[6] ? [w[6], 'champion'] : null;
  return null;
}

// The set of team-color names that field at least one tracked unit. `teams` is
// the API Teams map ({ red: { Units:[…] }, … }); `trackedLower` is a Set of
// lowercased usernames to watch.
export function teamsWithTrackedUnits(teams, trackedLower) {
  const out = new Set();
  if (!teams || !trackedLower || !trackedLower.size) return out;
  for (const color in teams) {
    const units = teams[color] && (teams[color].Units || teams[color].units);
    if (!units) continue;
    for (const u of units) {
      if (trackedLower.has((u.Name || '').toLowerCase())) { out.add(color); break; }
    }
  }
  return out;
}

// Compare a previous and current poll snapshot and return the list of sound
// events to fire for the user's tracked teams. Pure — the caller plays each via
// playEventIfEnabled and stores `cur` as the next `prev`.
//
//   snapshot = { matchIndex, winners }  (matchIndex = currentMatchIndex, -1 done)
// Fires:
//   matchUp — a tracked team's match becomes the current match
//   win/loss — a tracked team's match resolves (winners[i] fills in)
//   champ  — a tracked team wins the champ match (winners[7])
export function matchEvents(prev, cur, myTeams) {
  const events = [];
  if (!prev || !myTeams || !myTeams.size) return events;
  const pw = prev.winners || [], cw = cur.winners || [];

  // matchUp: the current match changed to one featuring a tracked team.
  if (cur.matchIndex >= 0 && cur.matchIndex !== prev.matchIndex) {
    const pair = matchTeams(cur.matchIndex, cw);
    if (pair && pair.some((t) => myTeams.has(t))) events.push('matchUp');
  }

  // win/loss/champ: any slot that newly resolved this poll.
  for (let i = 0; i < 8; i++) {
    if (cw[i] && !pw[i]) {
      const pair = matchTeams(i, cw);
      if (!pair) continue;
      const mine = pair.find((t) => myTeams.has(t));
      if (!mine) continue;
      if (cw[i] === mine) events.push(i === 7 ? 'champ' : 'win');
      else events.push('loss');
    }
  }
  return events;
}
